import React, { useState, useRef, useEffect } from "react";
import {
    GoogleMap,
    LoadScript,
    Marker,
    Autocomplete,
    Circle,
} from "@react-google-maps/api";

const containerStyle = {
    width: "100%",
    height: "400px",
};

const GoogleMapsStops = ({ stops, setStops, validation, newStop }) => {
    const [mapCenter, setMapCenter] = useState({
        lat: validation.values.lat || 29.42,
        lng: validation.values.lng || -98.49,
    });

    const [currentStop, setCurrentStop] = useState({
        lat: validation.values.lat,
        lng: validation.values.lng,
    });

    const autocompleteRef = useRef(null);
    const mapRef = useRef(null); // 🔹 referencia al mapa

    // -------------------------------------------
    // Helpers
    // -------------------------------------------
    const milesToMeters = (miles) => miles * 1609.34;

    const geofenceMiles = validation.values.geofence || 0.25;

    const handleGeofenceChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        validation.setFieldValue("geofence", value);

        setCurrentStop((prev) => ({
            ...prev,
            geofence: value,
        }));
    };

    // -------------------------------------------
    // Distance calculation
    // -------------------------------------------
    const calculateDistance = async (origin, destination) => {
        const service = new window.google.maps.DistanceMatrixService();

        return new Promise((resolve, reject) => {
            service.getDistanceMatrix(
                {
                    origins: [origin],
                    destinations: [destination],
                    travelMode: "DRIVING",
                    unitSystem: window.google.maps.UnitSystem.METRIC,
                },
                (response, status) => {
                    if (status === "OK" && response.rows[0].elements[0].distance) {
                        const meters = response.rows[0].elements[0].distance.value;
                        const miles = (meters / 1609.34).toFixed(2);
                        resolve(miles);
                    } else {
                        reject("Error calculating distance");
                    }
                }
            );
        });
    };

    // -------------------------------------------
    // Autocomplete search
    // -------------------------------------------
    const handlePlaceChanged = async () => {
        const place = autocompleteRef.current.getPlace();

        if (!place?.geometry) return;

        const newCoordinates = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
        };

        validation.setFieldValue("name", place.formatted_address);
        validation.setFieldValue("lat", newCoordinates.lat);
        validation.setFieldValue("lng", newCoordinates.lng);

        setCurrentStop((prev) => ({ ...prev, ...newCoordinates }));
        setMapCenter(newCoordinates);

        calculateMilesFromPreviousStop(newCoordinates);
    };

    // -------------------------------------------
    // Map click → move marker + calculate miles
    // -------------------------------------------
    const handleMapClick = async (event) => {
        const newCoordinates = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
        };

        validation.setFieldValue("lat", newCoordinates.lat);
        validation.setFieldValue("lng", newCoordinates.lng);

        setCurrentStop((prev) => ({ ...prev, ...newCoordinates }));
        setMapCenter(newCoordinates);

        calculateMilesFromPreviousStop(newCoordinates);
    };

    const calculateMilesFromPreviousStop = async (coords) => {
        const currentOrder = stops.length + 1;
        const previousStop = stops.find((stop) => stop.order === currentOrder - 1);

        if (previousStop) {
            const origin = new window.google.maps.LatLng(
                previousStop.lat,
                previousStop.lng
            );
            const destination = new window.google.maps.LatLng(
                coords.lat,
                coords.lng
            );

            try {
                const distance = await calculateDistance(origin, destination);
                validation.setFieldValue("mile", distance);
            } catch (error) {
                console.error(error);
            }
        } else {
            validation.setFieldValue("mile", 0);
        }
    };

    // -------------------------------------------
    // Fit bounds to stops
    // -------------------------------------------
    const fitMapToStops = (stopsToFit) => {
        if (!mapRef.current) return;
        if (!stopsToFit || !stopsToFit.length) return;
        if (!window.google) return;

        const bounds = new window.google.maps.LatLngBounds();

        stopsToFit.forEach((stop) => {
            if (!stop.lat || !stop.lng) return;

            bounds.extend({
                lat: parseFloat(stop.lat),
                lng: parseFloat(stop.lng),
            });
        });

        // Si solo hay un punto, Google a veces se acerca demasiado,
        // puedes controlar zoom manual si quieres.
        mapRef.current.fitBounds(bounds);
    };

    const handleMapLoad = (map) => {
        mapRef.current = map;

        // Cuando se carga el mapa, si ya hay stops, ajusta bounds
        if (stops && stops.length > 0) {
            fitMapToStops(stops);
        }
    };

    // Opcional: si cambia la lista de stops, vuelve a ajustar el mapa
    useEffect(() => {
        if (stops && stops.length > 0) {
            fitMapToStops(stops);
        }
    }, [stops]);

    // -------------------------------------------
    // Render
    // -------------------------------------------
    return (
        <LoadScript
            googleMapsApiKey="AIzaSyD2ihNBZrNGhWURi4BSk53T5lsgrJ08ciA"
            libraries={["places"]}
        >
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={10}
                onClick={handleMapClick}
                onLoad={handleMapLoad} // 🔹 aquí conectamos el mapa
            >
                {/* Existing stops */}
                {stops.map((stop, index) => (
                    <Marker
                        key={index}
                        position={{
                            lat: parseFloat(stop.lat),
                            lng: parseFloat(stop.lng),
                        }}
                        label={{
                            text: `${stop.order}`,
                            color: "white",
                            fontSize: "14px",
                        }}
                    />
                ))}

                {/* Current stop marker + geofence */}
                {currentStop?.lat && (
                    <>
                        <Marker
                            position={{ lat: currentStop.lat, lng: currentStop.lng }}
                            label={{
                                text: "?",
                                color: "red",
                                fontSize: "14px",
                            }}
                        />

                        <Circle
                            center={{
                                lat: currentStop.lat,
                                lng: currentStop.lng,
                            }}
                            radius={milesToMeters(geofenceMiles)}
                            options={{
                                strokeColor: "#FF7A00",
                                strokeOpacity: 0.8,
                                strokeWeight: 2,
                                fillColor: "#FF7A00",
                                fillOpacity: 0.15,
                                clickable: false,
                            }}
                        />
                    </>
                )}
            </GoogleMap>

            {/* Address input + geofence control */}
            {newStop && (
                <div className="mt-4 space-y-4">
                    {/* Address */}
                    <div>
                        <label className="block text-sm text-[#ADADAD] mb-1">
                            Address
                        </label>

                        <Autocomplete
                            onLoad={(ref) => (autocompleteRef.current = ref)}
                            onPlaceChanged={handlePlaceChanged}
                        >
                            <input
                                type="text"
                                placeholder="Enter a location"
                                className="p-2 border border-gray-300 rounded w-full"
                                onChange={(e) =>
                                    validation.setFieldValue("name", e.target.value)
                                }
                            />
                        </Autocomplete>
                    </div>

                    {/* Geofence radius control */}
                    <div>
                        <label className="block text-sm font-medium text-cs-dark mb-1">
                            Geofence Radius (miles)
                        </label>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 w-20">
                                <input
                                    type="number"
                                    min="0.1"
                                    max="5"
                                    step="0.1"
                                    value={geofenceMiles}
                                    onChange={handleGeofenceChange}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                                <span className="text-xs text-cs-muted">mi</span>
                            </div>
                            <input
                                type="range"
                                min="0.1"
                                max="5"
                                step="0.1"
                                value={geofenceMiles}
                                onChange={handleGeofenceChange}
                                className="w-full"
                            />
                        </div>

                        <p className="mt-1 text-[11px] text-cs-muted">
                            {milesToMeters(geofenceMiles).toFixed(0)} meters radius
                        </p>
                    </div>
                </div>
            )}
        </LoadScript>
    );
};

export default GoogleMapsStops;
